export const SUPPORTED_WIDGET_ICON_NAMES = [
  'Activity',
  'AlertCircle',
  'AlertTriangle',
  'ArrowLeftRight',
  'ArrowRightLeft',
  'Battery',
  'Beef',
  'Beer',
  'Calendar',
  'Calculator',
  'Check',
  'CheckCircle',
  'ChefHat',
  'Clock',
  'Cpu',
  'CreditCard',
  'DollarSign',
  'Droplet',
  'Eye',
  'Gift',
  'Globe',
  'Grid',
  'Home',
  'IceCream',
  'Info',
  'Layers',
  'LayoutGrid',
  'Leaf',
  'Lock',
  'Package',
  'Receipt',
  'Ruler',
  'Scale',
  'Search',
  'Shield',
  'ShieldCheck',
  'Smartphone',
  'Snowflake',
  'Sparkles',
  'Star',
  'Sun',
  'Thermometer',
  'TrendingUp',
  'Truck',
  'Utensils',
  'Wine',
  'Wrench',
  'Zap',
] as const

export type WidgetIconName = (typeof SUPPORTED_WIDGET_ICON_NAMES)[number]

export type WidgetIconSource =
  | 'live-legacy'
  | 'database'
  | 'slug-override'
  | 'category-fallback'
  | 'generic-fallback'

export interface WidgetIconTool {
  slug: string
  category: string | null
  icon_name: string | null
}

export interface WidgetIconResolution {
  iconName: WidgetIconName
  source: WidgetIconSource
}

export const GENERIC_WIDGET_FALLBACK_ICON: WidgetIconName = 'Calculator'

const SUPPORTED_WIDGET_ICON_NAME_SET = new Set<string>(SUPPORTED_WIDGET_ICON_NAMES)

// Mirrors the current production /widgets surface for the original public widget set.
export const LIVE_LEGACY_ICON_BY_SLUG: Record<string, WidgetIconName> = {
  'energy-cost-calculator': 'Zap',
  'refrigerator-energy-efficiency-calculator': 'Zap',
  'refrigerator-carbon-footprint-calculator': 'Globe',
  'appliance-energy-calculator': 'Zap',
  'auto-vs-manual-defrost-cost-calculator': 'Snowflake',
  'refrigerator-size-calculator': 'Ruler',
  'kitchen-space-planner': 'LayoutGrid',
  'freezer-btu-calculator': 'Snowflake',
  'refrigerator-lifespan-calculator': 'Clock',
  'food-storage-calculator': 'Utensils',
  'recipe-measurement-converter': 'Utensils',
  'recipe-serving-size-adjuster': 'Utensils',
  'cooking-temperature-converter': 'Thermometer',
  'wine-refrigerator-calculator': 'Wine',
  'beverage-cooler-size-calculator': 'Wine',
  'ice-maker-capacity-calculator': 'Snowflake',
  'kitchen-timer': 'Clock',
}

// Derived from the first non-Calculator/Home/Zap lucide-react import in each tool page-client.
export const SLUG_OVERRIDE_ICON_BY_SLUG: Record<string, WidgetIconName> = {
  'appliance-energy-calculator': 'DollarSign',
  'auto-vs-manual-defrost-cost-calculator': 'Snowflake',
  'beverage-cooler-size-calculator': 'Beer',
  'chest-freezer-basket-guide': 'Package',
  'cooking-temperature-converter': 'Thermometer',
  'display-cooler-calculator': 'Eye',
  'dorm-mini-fridge-calculator': 'AlertTriangle',
  'dual-compressor-refrigerator-calculator': 'Cpu',
  'dual-zone-wine-cooler-calculator': 'Thermometer',
  'energy-cost-calculator': 'DollarSign',
  'energy-star-savings-calculator': 'Star',
  'family-refrigerator-size-calculator': 'Ruler',
  'financing-comparison-tool': 'ArrowLeftRight',
  'find-your-fridge': 'Search',
  'food-storage-calculator': 'Package',
  'food-waste-calculator': 'DollarSign',
  'freezer-btu-calculator': 'Thermometer',
  'freezer-burn-prevention-guide': 'Shield',
  'freezer-dry-ice-calculator': 'AlertTriangle',
  'freezer-energy-cost-calculator': 'Snowflake',
  'freezer-meat-storage-calculator': 'Beef',
  'freezer-storage-capacity-calculator': 'Package',
  'freezer-vacuum-sealer-calculator': 'Package',
  'french-door-vs-side-by-side-refrigerator': 'ArrowLeftRight',
  'garage-freezer-size-calculator': 'AlertTriangle',
  'holiday-food-storage-calculator': 'Gift',
  'ice-maker-capacity-calculator': 'Snowflake',
  'ice-maker-energy-cost-calculator': 'IceCream',
  'ice-maker-maintenance-schedule': 'Calendar',
  'ice-maker-size-calculator': 'Ruler',
  'kegerator-beer-line-calculator': 'Activity',
  'kegerator-keg-yield-calculator': 'Beer',
  'kegerator-size-calculator': 'Beer',
  'kimchi-refrigerator-calculator': 'Leaf',
  'kitchen-space-planner': 'Ruler',
  'kitchen-timer': 'Clock',
  'medical-refrigerator-guide': 'Activity',
  'nugget-vs-cube-ice-maker-comparison': 'Check',
  'outdoor-refrigerator-btu-calculator': 'Sun',
  'power-outage-food-safety-guide': 'CheckCircle',
  'recipe-measurement-converter': 'ArrowRightLeft',
  'recipe-serving-size-adjuster': 'ChefHat',
  'refrigerator-age-calculator': 'Calendar',
  'refrigerator-carbon-footprint-calculator': 'Leaf',
  'refrigerator-coil-cleaning-savings-calculator': 'Sparkles',
  'refrigerator-comparison-tool': 'Check',
  'refrigerator-compressor-amp-calculator': 'AlertCircle',
  'refrigerator-cost-of-ownership-calculator': 'DollarSign',
  'refrigerator-door-energy-loss-calculator': 'AlertTriangle',
  'refrigerator-door-seal-test': 'AlertTriangle',
  'refrigerator-electricity-cost-estimator': 'Receipt',
  'refrigerator-energy-efficiency-calculator': 'Star',
  'refrigerator-financing-calculator': 'DollarSign',
  'refrigerator-gasket-replacement-cost': 'Wrench',
  'refrigerator-generator-size-calculator': 'Battery',
  'refrigerator-leveling-guide': 'AlertCircle',
  'refrigerator-lifespan-calculator': 'Clock',
  'refrigerator-lock-comparison': 'Lock',
  'refrigerator-maintenance-schedule': 'Calendar',
  'refrigerator-noise-level-calculator': 'Info',
  'refrigerator-organization-planner': 'LayoutGrid',
  'refrigerator-payment-estimator': 'CreditCard',
  'refrigerator-rebate-finder': 'DollarSign',
  'refrigerator-recall-checker': 'ShieldCheck',
  'refrigerator-refrigerant-leak-guide': 'AlertTriangle',
  'refrigerator-repair-cost-estimator': 'Wrench',
  'refrigerator-repair-vs-replace-calculator': 'Wrench',
  'refrigerator-size-calculator': 'Ruler',
  'refrigerator-temperature-alarm-guide': 'AlertTriangle',
  'refrigerator-temperature-recovery-calculator': 'AlertCircle',
  'refrigerator-temperature-settings-guide': 'Thermometer',
  'refrigerator-temperature-zone-guide': 'Thermometer',
  'refrigerator-upgrade-savings-calculator': 'TrendingUp',
  'refrigerator-warranty-comparison': 'Check',
  'refrigerator-water-filter-cost-calculator': 'Droplet',
  'refrigerator-water-filter-finder': 'Search',
  'refrigerator-water-line-pressure-calculator': 'AlertCircle',
  'repair-or-replace-refrigerator': 'Scale',
  'rv-refrigerator-size-calculator': 'Truck',
  'scratch-dent-value-calculator': 'AlertCircle',
  'smart-refrigerator-worth-it-calculator': 'Smartphone',
  'under-counter-refrigerator-fit-calculator': 'Ruler',
  'upright-freezer-shelf-planner': 'Grid',
  'walk-in-cooler-calculator': 'Ruler',
  'warranty-coverage-lookup': 'Shield',
  'warranty-expiration-calculator': 'Shield',
  'wine-cooler-energy-calculator': 'Layers',
  'wine-refrigerator-calculator': 'Wine',
}

export const CATEGORY_FALLBACK_ICON_BY_CATEGORY: Record<string, WidgetIconName> = {
  'Energy & Savings': 'DollarSign',
  'Energy & Cost': 'DollarSign',
  'Cost & Value': 'DollarSign',
  'Food Storage & Safety': 'Package',
  'Freezer Organization': 'Package',
  'Size & Capacity': 'Ruler',
  'Sizing & Capacity': 'Ruler',
  'Size & Planning': 'Ruler',
  'Technical & Performance': 'Ruler',
  'Recipe Tools': 'Utensils',
  'Beverage & Specialty': 'Wine',
  Maintenance: 'Wrench',
}

export function isWidgetIconName(value: string | null | undefined): value is WidgetIconName {
  return Boolean(value) && SUPPORTED_WIDGET_ICON_NAME_SET.has(value)
}

export function resolveWidgetIconName(tool: WidgetIconTool): WidgetIconResolution {
  const legacyIcon = LIVE_LEGACY_ICON_BY_SLUG[tool.slug]
  if (legacyIcon) {
    return { iconName: legacyIcon, source: 'live-legacy' }
  }

  if (tool.icon_name && tool.icon_name !== GENERIC_WIDGET_FALLBACK_ICON && isWidgetIconName(tool.icon_name)) {
    return { iconName: tool.icon_name, source: 'database' }
  }

  const overrideIcon = SLUG_OVERRIDE_ICON_BY_SLUG[tool.slug]
  if (overrideIcon) {
    return { iconName: overrideIcon, source: 'slug-override' }
  }

  if (tool.category === 'Cooking Essentials') {
    return {
      iconName: tool.slug === 'kitchen-timer' ? 'Clock' : 'Thermometer',
      source: 'category-fallback',
    }
  }

  const categoryIcon = tool.category ? CATEGORY_FALLBACK_ICON_BY_CATEGORY[tool.category] : undefined
  if (categoryIcon) {
    return { iconName: categoryIcon, source: 'category-fallback' }
  }

  return { iconName: GENERIC_WIDGET_FALLBACK_ICON, source: 'generic-fallback' }
}

export default {
  CATEGORY_FALLBACK_ICON_BY_CATEGORY,
  GENERIC_WIDGET_FALLBACK_ICON,
  LIVE_LEGACY_ICON_BY_SLUG,
  SLUG_OVERRIDE_ICON_BY_SLUG,
  SUPPORTED_WIDGET_ICON_NAMES,
  isWidgetIconName,
  resolveWidgetIconName,
}
